import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  Activity,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Play,
  AlertTriangle,
  Trash2,
} from "lucide-react";
import { webhookCardapioService } from "../../../../services/webhookCardapioService";

const FilaMonitor: React.FC = () => {
  const queryClient = useQueryClient();
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [showConfirmLimpar, setShowConfirmLimpar] = useState(false);

  const { data: filaStatus, isLoading } = useQuery({
    queryKey: ["fila-status"],
    queryFn: webhookCardapioService.getFilaStatus,
    refetchInterval: autoRefresh ? 3000 : false,
  });

  const { data: pedidosPendentes } = useQuery({
    queryKey: ["pedidos-pendentes"],
    queryFn: () => webhookCardapioService.getPedidos(1, 10, "pending"),
    refetchInterval: autoRefresh ? 5000 : false,
  });

  const { data: pedidosErro } = useQuery({
    queryKey: ["pedidos-erro"],
    queryFn: () => webhookCardapioService.getPedidos(1, 10, "error"),
    refetchInterval: autoRefresh ? 5000 : false,
  });

  const reprocessarMutation = useMutation({
    mutationFn: webhookCardapioService.reprocessarPendentes,
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries({ queryKey: ["fila-status"] });
      queryClient.invalidateQueries({ queryKey: ["pedidos-pendentes"] });
      queryClient.invalidateQueries({ queryKey: ["pedidos-erro"] });
    },
    onError: (error: Error) => {
      toast.error(`Erro ao reprocessar: ${error.message}`);
    },
  });

  const processarAntigosMutation = useMutation({
    mutationFn: webhookCardapioService.processarWebhooksAntigos,
    onSuccess: (data) => {
      if (data.enfileirados === 0) {
        toast.info(
          "Nenhum webhook para processar. Todos já foram processados!"
        );
      } else {
        toast.success(
          `${data.enfileirados} webhooks enfileirados! (${data.nuncaProcessados} novos + ${data.comStatusPending} com status pending)`
        );
      }
      queryClient.invalidateQueries({ queryKey: ["fila-status"] });
      queryClient.invalidateQueries({ queryKey: ["pedidos-pendentes"] });
      queryClient.invalidateQueries({ queryKey: ["pedidos-erro"] });
    },
    onError: (error: Error) => {
      toast.error(`Erro ao processar webhooks: ${error.message}`);
    },
  });

  const limparFilaMutation = useMutation({
    mutationFn: webhookCardapioService.limparFila,
    onSuccess: (data) => {
      toast.success(
        `Fila limpa com sucesso! ${data.before.total} jobs removidos.`
      );
      setShowConfirmLimpar(false);
      queryClient.invalidateQueries({ queryKey: ["fila-status"] });
      queryClient.invalidateQueries({ queryKey: ["pedidos-pendentes"] });
      queryClient.invalidateQueries({ queryKey: ["pedidos-erro"] });
    },
    onError: (error: Error) => {
      toast.error(`Erro ao limpar fila: ${error.message}`);
      setShowConfirmLimpar(false);
    },
  });

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ["fila-status"] });
    queryClient.invalidateQueries({ queryKey: ["pedidos-pendentes"] });
    queryClient.invalidateQueries({ queryKey: ["pedidos-erro"] });
    toast.success("Dados atualizados!");
  };

  const stats = filaStatus?.stats;

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Activity className="text-primary" size={20} />
          </div>
          <h3 className="text-lg font-semibold text-neutral-900">
            Monitoramento da Fila de Pedidos
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              autoRefresh
                ? "bg-green-100 text-green-700 hover:bg-green-200"
                : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
            }`}
          >
            <Play size={14} />
            {autoRefresh ? "Auto-refresh ON" : "Auto-refresh OFF"}
          </button>
          <button
            type="button"
            onClick={handleRefresh}
            className="flex items-center gap-2 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
          >
            <RefreshCw size={14} />
            Atualizar
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-neutral-500">Carregando...</div>
      ) : stats ? (
        <>
          <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
              <div className="flex items-center gap-2 text-yellow-700">
                <Clock size={18} />
                <span className="text-xs font-medium uppercase">
                  Aguardando
                </span>
              </div>
              <div className="mt-2 text-2xl font-bold text-yellow-900">
                {stats.waiting}
              </div>
            </div>

            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-center gap-2 text-blue-700">
                <Activity size={18} />
                <span className="text-xs font-medium uppercase">
                  Processando
                </span>
              </div>
              <div className="mt-2 text-2xl font-bold text-blue-900">
                {stats.active}
              </div>
            </div>

            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle size={18} />
                <span className="text-xs font-medium uppercase">
                  Completados
                </span>
              </div>
              <div className="mt-2 text-2xl font-bold text-green-900">
                {stats.completed}
              </div>
            </div>

            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <div className="flex items-center gap-2 text-red-700">
                <XCircle size={18} />
                <span className="text-xs font-medium uppercase">Falhados</span>
              </div>
              <div className="mt-2 text-2xl font-bold text-red-900">
                {stats.failed}
              </div>
            </div>

            <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
              <div className="flex items-center gap-2 text-orange-700">
                <AlertTriangle size={18} />
                <span className="text-xs font-medium uppercase">Atrasados</span>
              </div>
              <div className="mt-2 text-2xl font-bold text-orange-900">
                {stats.delayed}
              </div>
            </div>

            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
              <div className="flex items-center gap-2 text-neutral-700">
                <Activity size={18} />
                <span className="text-xs font-medium uppercase">Total</span>
              </div>
              <div className="mt-2 text-2xl font-bold text-neutral-900">
                {stats.total}
              </div>
            </div>
          </div>

          {((pedidosErro?.total ?? 0) > 0 ||
            (pedidosPendentes?.total ?? 0) > 0) && (
            <div className="mb-6 rounded-lg border border-orange-200 bg-orange-50 p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h4 className="mb-1 font-semibold text-orange-900">
                    Pedidos que precisam de atenção
                  </h4>
                  <p className="text-sm text-orange-700">
                    {pedidosErro?.total ?? 0} pedidos com erro e{" "}
                    {pedidosPendentes?.total ?? 0} pendentes. Clique em
                    "Reprocessar" para tentar buscar os detalhes novamente.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => reprocessarMutation.mutate()}
                  disabled={reprocessarMutation.isPending}
                  className="flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-orange-700 disabled:bg-orange-400"
                >
                  <RefreshCw
                    size={14}
                    className={
                      reprocessarMutation.isPending ? "animate-spin" : ""
                    }
                  />
                  {reprocessarMutation.isPending
                    ? "Reprocessando..."
                    : "Reprocessar"}
                </button>
              </div>
            </div>
          )}

          <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="mb-1 font-semibold text-blue-900">
                  Processar webhooks antigos
                </h4>
                <p className="text-sm text-blue-700">
                  Processa webhooks confirmados que:
                </p>
                <ul className="mt-1 list-inside list-disc text-sm text-blue-700">
                  <li>Nunca foram processados (não estão na tabela)</li>
                  <li>Estão com status "pending" (precisam reprocessar)</li>
                </ul>
                <p className="mt-1 text-xs text-blue-600">
                  Útil para recuperar pedidos antigos ou reprocessar pendentes.
                </p>
              </div>
              <button
                type="button"
                onClick={() => processarAntigosMutation.mutate()}
                disabled={processarAntigosMutation.isPending}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:bg-blue-400"
              >
                <RefreshCw
                  size={14}
                  className={
                    processarAntigosMutation.isPending ? "animate-spin" : ""
                  }
                />
                {processarAntigosMutation.isPending
                  ? "Processando..."
                  : "Processar Antigos"}
              </button>
            </div>
          </div>

          {stats.total > 0 && (
            <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h4 className="mb-1 font-semibold text-red-900">
                    ⚠️ Limpar Fila Completa (Zona de Perigo)
                  </h4>
                  <p className="text-sm text-red-700">
                    Remove TODOS os {stats.total} jobs da fila Redis
                    (aguardando, processando, completados, falhados).
                  </p>
                  <p className="mt-1 text-xs font-medium text-red-800">
                    ⚠️ Esta ação não pode ser desfeita! Use apenas se tiver
                    certeza.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowConfirmLimpar(true)}
                  disabled={limparFilaMutation.isPending}
                  className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:bg-red-400"
                >
                  <Trash2 size={14} />
                  Limpar Fila
                </button>
              </div>
            </div>
          )}

          {showConfirmLimpar && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                    <AlertTriangle className="text-red-600" size={24} />
                  </div>
                  <h3 className="text-lg font-semibold text-neutral-900">
                    Confirmar Limpeza da Fila
                  </h3>
                </div>
                <p className="mb-2 text-sm text-neutral-700">
                  Você está prestes a remover{" "}
                  <strong>{stats.total} jobs</strong> da fila Redis:
                </p>
                <ul className="mb-4 list-inside list-disc space-y-1 text-sm text-neutral-700">
                  <li>{stats.waiting} aguardando</li>
                  <li>{stats.active} processando</li>
                  <li>{stats.completed} completados</li>
                  <li>{stats.failed} falhados</li>
                  <li>{stats.delayed} atrasados</li>
                </ul>
                <p className="mb-6 text-sm font-semibold text-red-600">
                  ⚠️ Esta ação é irreversível! Os jobs serão perdidos
                  permanentemente.
                </p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowConfirmLimpar(false)}
                    disabled={limparFilaMutation.isPending}
                    className="flex-1 rounded-lg border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={() => limparFilaMutation.mutate()}
                    disabled={limparFilaMutation.isPending}
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:bg-red-400"
                  >
                    <Trash2 size={14} />
                    {limparFilaMutation.isPending
                      ? "Limpando..."
                      : "Sim, Limpar Fila"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {filaStatus.activeJobs.length > 0 && (
            <div>
              <h4 className="mb-3 font-semibold text-neutral-900">
                Jobs em processamento ({filaStatus.activeJobs.length})
              </h4>
              <div className="space-y-2">
                {filaStatus.activeJobs.map((job) => (
                  <div
                    key={job.id}
                    className="flex items-center justify-between rounded-lg border border-neutral-200 bg-neutral-50 p-3"
                  >
                    <div>
                      <div className="font-medium text-neutral-900">
                        Pedido #{job.data.orderId}
                      </div>
                      <div className="text-xs text-neutral-600">
                        Job ID: {job.id} • Tentativa: {job.attemptsMade + 1}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-blue-600">
                      <Activity size={16} className="animate-spin" />
                      <span className="text-sm font-medium">Processando</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {stats.waiting === 0 &&
            stats.active === 0 &&
            filaStatus.activeJobs.length === 0 && (
              <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-center">
                <CheckCircle
                  className="mx-auto mb-2 text-green-600"
                  size={24}
                />
                <p className="font-medium text-green-900">Fila vazia!</p>
                <p className="text-sm text-green-700">
                  Todos os pedidos foram processados com sucesso.
                </p>
              </div>
            )}
        </>
      ) : (
        <div className="py-8 text-center text-neutral-500">
          Erro ao carregar status da fila
        </div>
      )}
    </div>
  );
};

export default FilaMonitor;
